/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from "react";
import to from "await-to-js";
import { observer } from "mobx-react";
import { fetchOrderDetail } from "@/app/csr-src/apis/order/order.api";
import { IOrderDetailReq, IOrderDetailRes } from "@/app/csr-src/apis/order/order.interface";
import OrderDetailStatus from "../orderDetailStatus";
import OrderDetailAmount from "../orderDetailAmount";
import OrderDetailProduct from "../orderDetailProduct";
import EmptyPage from "../emptyPage";
import ErrorPage from "../errorPage";
import UKPageLoading from "@/common-components-src/components/UKPageLoading";

interface orderDetailComponentsProps {
  orderId: string;
  isHideFloor?: boolean;
  handleInitCartNum?: () => void;
  pageLoadedHandler?: (productSkuIds: string[]) => void;
  changePageHandler?: (value: boolean) => void;
}

const orderDetailComponents: React.FC<orderDetailComponentsProps> = observer(
  ({ orderId, isHideFloor, handleInitCartNum, pageLoadedHandler, changePageHandler }) => {
    const [orderDetails, setOrderDetails] = useState<IOrderDetailRes | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    if (!orderId) {
      return null;
    }

    const loadOrderDetails = async () => {
      const requestData: IOrderDetailReq = { orderId: String(orderId) };
      const [err, orderDetailData] = await to(fetchOrderDetail(requestData));

      if (err) {
        setError("Failed to fetch order details");
        setOrderDetails(null);
      } else {
        if (orderDetailData?.orderStatusDTO?.recycle) {
          setOrderDetails(null);
          changePageHandler && changePageHandler(true);
        } else {
          setOrderDetails(orderDetailData as IOrderDetailRes);
          changePageHandler && changePageHandler(false);
        }
        setError(null);
      }
      const wareList = orderDetailData?.wareInfoDTOList.map((item) => item?.wareId) || [];
      pageLoadedHandler && pageLoadedHandler(wareList as any);
      setLoading(false);
    };

    useEffect(() => {
      loadOrderDetails();
    }, [orderId]);
    const {
      orderStatusDTO = {},
      buttonInfoDTOList = [],
      orderIdInfoDTO,
      deliveryinfoDTO,
      paymentInfoDTO,
      invoiceInfoDTO,
      addressInfoDTO,
      priceInfoDTO = {},
      wareInfoDTOList = [],
      logisticsInfoDTO = {},
      showCancelReason,
      showAfsProgressInfo,
      showCancelInfo,
      cancelInfoDTO = {},
      orderTagContentVO,
    } = orderDetails || {};
    const { orderStatusId } = orderStatusDTO || {};
    const { postCode = "" } = addressInfoDTO || {};

    if (loading) {
      return <UKPageLoading style={{ backgroundColor: "transparent" }} text="Loading" />;
    }

    // 订单明细数据处理
    const orderDetailDataHandler = () => {
      // JDISVOrderListOrderStatusTypeWaitingPay = 1, //待支付、等待付款确认
      // JDISVOrderListOrderStatusTypeWaitingDelivery = 3, //待发货
      // JDISVOrderListOrderStatusTypeCompleted = 5, //已完成
      // JDISVOrderListOrderStatusTypeCancelled = 6, //已取消
      // JDISVOrderListOrderStatusTypeWaitingSelfDelivery = 7, //等待自提
      // JDISVOrderListOrderStatusTypeWaitingReceipt = 8, //待收货
      let list = null;
      if (orderStatusId === "1") {
        // Unpaid
        list = [
          // Order ID
          {
            label: orderIdInfoDTO?.orderIdTitle || "",
            value: orderIdInfoDTO?.orderId || "",
            showCopyButton: orderIdInfoDTO?.showCopyButton,
            copyButtonTitle: orderIdInfoDTO?.copyButtonTitle,
          },
          // Delivery address
          {
            label: addressInfoDTO?.addressTitle || "",
            value: addressInfoDTO?.address || "",
            customerName: addressInfoDTO?.customerName,
            mobile: addressInfoDTO?.mobile,
          },
          // Billing address
          {
            label: invoiceInfoDTO?.invoiceAddressTitle || "",
            value: invoiceInfoDTO?.invoiceAddress || "",
            customerName: invoiceInfoDTO?.invoiceUserName,
            mobile: invoiceInfoDTO?.invoiceUserPhone,
            isExpand: true,
          },
        ];
      } else if (orderStatusId === "3" || orderStatusId === "8" || orderStatusId === "6" || orderStatusId === "5") {
        // Processing Shipped Cancelled Completed
        list = [
          // Order ID
          {
            label: orderIdInfoDTO?.orderIdTitle || "",
            value: orderIdInfoDTO?.orderId || "",
            showCopyButton: orderIdInfoDTO?.showCopyButton,
            copyButtonTitle: orderIdInfoDTO?.copyButtonTitle,
          },
          // Order time
          {
            // label: orderIdInfoDTO?.submitDateTitle || "",
            // value: orderIdInfoDTO?.submitDate || "",
            label: paymentInfoDTO?.paymentTimeTitle || "",
            value: paymentInfoDTO?.paymentTime || "",
          },
          // Payment method
          {
            label: paymentInfoDTO?.paymentTitle || "",
            value: paymentInfoDTO?.payMode || "",
          },
          // Delivery address
          {
            label: addressInfoDTO?.addressTitle || "",
            value: addressInfoDTO?.address || "",
            customerName: addressInfoDTO?.customerName,
            mobile: addressInfoDTO?.mobile,
          },
          // Billing address
          {
            label: invoiceInfoDTO?.invoiceAddressTitle || "",
            value: invoiceInfoDTO?.invoiceAddress || "",
            // customerName: invoiceInfoDTO?.customerName,
            // mobile: invoiceInfoDTO?.secretMobile?.plain,
            customerName: invoiceInfoDTO?.invoiceUserName,
            mobile: invoiceInfoDTO?.invoiceUserPhone,
            isExpand: true,
          },
        ];
      }

      return list;
    };

    if (error) {
      return <ErrorPage />;
    }

    if (!orderDetails) {
      return <EmptyPage />;
    }

    return (
      <>
        {/* 订单状态楼层 */}
        {isHideFloor ? null : (
          <OrderDetailStatus
            orderInfo={{
              orderId,
              ...orderDetails,
            }}
            onRefresh={loadOrderDetails}
          />
        )}
        {/* 订单金额信息+非金额信息楼层 */}
        <OrderDetailAmount orderInfo={{ detailInfo: orderDetailDataHandler(), priceInfoDTO, orderStatusDTO, orderId }} />
        {/* 订单商品信息楼层 */}
        <OrderDetailProduct
          orderInfo={{ orderStatusDTO, wareInfoDTOList, orderId, postCode, orderTagContentVO }}
          handleInitCartNum={handleInitCartNum}
          onRefresh={loadOrderDetails}
        />
      </>
    );
  },
);

export default orderDetailComponents;
